import { useEffect, useMemo, useState } from 'react'
import { formatPhoneNumber, formatPhoneWithLabel, normalizePhoneKey } from 'app/formatPhoneNumber'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { ObjectId } from 'lib/ObjectId'
import { gqlQuery } from 'app/lib/graphqlClient'
import { DNI_POOL_NUMBERS_QUERY } from 'app/components/dni/graphql'
import { debug } from 'lib/logging'
import { OptionType } from 'types/OptionType'
import { IvrProvisioningModal } from '../IvrProvisioningModal'
import { DNI_FOR_MODAL_QUERY, DNI_MANAGED_NUMBERS_QUERY } from './graphql'
import { extractAreaCode } from './dniSchema'
import type { DniData, ManagedNumber } from 'app/types/dni'

export const PURCHASE_OPTION_ID = `__purchase_new__`

export type UseDniFormArgs = {
  dniId?: string
  locationId?: string
  locationName?: string
  onAdd: () => void
}

export type UseDniFormReturn = {
  isEditing: boolean
  mode: 'form' | 'summary'
  location?: OptionType
  dniData?: DniData
  maxPoolSizeValue: string
  poolSizeValue: string
  areaCode: string
  removing: boolean
  closing: boolean
  submitting: boolean
  busy: boolean
  loadingValues: boolean
  newDniId: string
  numberOptions: OptionType[]
  selectedNumber?: OptionType
  comboValue?: OptionType
  replacesNumberDisplay: string
  snippetCode: string
  locationName?: string
  locationId?: string
  dniId?: string
  setMode: (mode: 'form' | 'summary') => void
  setLocation: (loc: OptionType | undefined) => void
  setMaxPoolSizeValue: (v: string) => void
  setPoolSizeValue: (v: string) => void
  setAreaCode: (v: string) => void
  setSelectedNumber: (opt: OptionType | undefined) => void
  onSubmitStart: () => void
  onCreateCompleted: (data: { success?: boolean; error?: string }) => void
  onDisable: () => void
  onPurchaseNumber: () => void
}

export function useDniForm({ dniId, locationId, locationName, onAdd }: UseDniFormArgs): UseDniFormReturn {
  const isEditing = !!dniId
  const [mode, setMode] = useState<'form' | 'summary'>(isEditing ? `summary` : `form`)
  const [location, setLocation] = useState<OptionType | undefined>(
    locationId ? { id: locationId, name: locationName || ``, value: locationId } : undefined,
  )
  const [managedNumbers, setManagedNumbers] = useState<ManagedNumber[]>([])
  const [areaCode, setAreaCode] = useState(``)
  const [dniData, setDniData] = useState<DniData | undefined>()
  // Live values for the auto-scale on/off indicator in the edit form.
  const [maxPoolSizeValue, setMaxPoolSizeValue] = useState(``)
  const [poolSizeValue, setPoolSizeValue] = useState(``)
  const [removing, setRemoving] = useState(false)
  // SmartForm does its own fetch (shouldPreventDefault), so RVF's useIsSubmitting never
  // flips. Track submit state locally so the Save button disables while the pool provisions.
  const [submitting, setSubmitting] = useState(false)
  const [closing, setClosing] = useState(false)
  const [selectedNumber, setSelectedNumber] = useState<OptionType | undefined>()
  const busy = submitting || removing || closing
  const loadingValues = useMemo(() => isEditing && !dniData, [isEditing, dniData])
  // Stable client-generated id for a new pool, so the embed code shows before saving.
  const newDniId = useMemo(() => ObjectId(), [])
  const user = useUser()
  const isSuperUser = user?.type === `super`

  // Load the managed numbers a pool can replace: any PhoneNumber for the location
  // that is NOT a pool or source tracker (those are DNI-internal).
  const labelForNumber = (phone?: string) =>
    managedNumbers.find((n) => normalizePhoneKey(n.phone) === normalizePhoneKey(phone))?.label

  const fetchManagedNumbers = (locId: string) => {
    bg(() =>
      gqlQuery(
        DNI_MANAGED_NUMBERS_QUERY,
        { search: `locationId:${locId}`, pagination: { limit: 100 } },
        { requestPolicy: `network-only` },
      ).then((result) => {
        const hits = result.data?.phoneNumbers?.hits || []
        const selectable = hits.filter((n) => !n.roles?.includes(`pool`) && !n.roles?.includes(`source`))
        setManagedNumbers(selectable.map((n) => ({ id: n.id, phone: n.phone, label: n.label ?? undefined })))
      }),
    )
  }

  // Load an existing DNI pool and its pool numbers when editing.
  const fetchDni = (id: string) => {
    bg(() =>
      Promise.all([
        gqlQuery(DNI_FOR_MODAL_QUERY, { id }, { requestPolicy: `network-only` }),
        gqlQuery(DNI_POOL_NUMBERS_QUERY, { dniId: id }, { requestPolicy: `network-only` }),
      ]).then(([dniResult, poolResult]) => {
        const dni = dniResult.data?.dni
        if (!dni) return
        const sortedByCreated = [...(poolResult.data?.dniPoolNumbers || [])].sort(
          (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
        )
        const startingCount = dni.poolSize ?? 0
        const poolNumbers = sortedByCreated.map((p, index) => ({
          phone: p.phone,
          formatted: formatPhoneNumber(p.phone) || p.phone,
          autoProvisioned: index >= startingCount,
        }))
        setDniData({
          id: dni.id,
          label: dni.label ?? undefined,
          forwardTo: dni.forwardTo,
          areaCode: dni.areaCode,
          poolSize: dni.poolSize,
          sessionDuration: dni.sessionDuration,
          maxPoolSize: dni.maxPoolSize ?? undefined,
          minFree: dni.minFree ?? undefined,
          buyStep: dni.buyStep ?? undefined,
          minPoolSize: dni.minPoolSize ?? undefined,
          ivrConfigId: dni.ivrConfigId ?? undefined,
          poolNumbers,
        })
        setMaxPoolSizeValue(dni.maxPoolSize ? dni.maxPoolSize.toString() : ``)
        setPoolSizeValue(dni.poolSize ? dni.poolSize.toString() : ``)
        setSelectedNumber({
          id: dni.forwardTo,
          name: formatPhoneWithLabel(dni.forwardTo, labelForNumber(dni.forwardTo)),
          value: dni.forwardTo,
        })
      }),
    )
  }

  useEffect(() => {
    if (location?.value) {
      setManagedNumbers([])
      if (!isEditing) setSelectedNumber(undefined)
      fetchManagedNumbers(location.value)
    }
  }, [location?.value])

  useEffect(() => {
    if (dniId) fetchDni(dniId)
  }, [dniId])

  // Combo value is the phone string (becomes DNI.forwardTo). Super users can buy a new AWS number.
  const numberOptions: OptionType[] = [
    ...managedNumbers.map((n) => ({
      id: n.phone,
      name: formatPhoneWithLabel(n.phone, n.label),
      value: n.phone,
    })),
    ...(isSuperUser && location?.value
      ? [{ id: PURCHASE_OPTION_ID, name: `+ Purchase new number`, value: PURCHASE_OPTION_ID }]
      : []),
  ]

  const selectableOptions = numberOptions.filter((o) => o.id !== PURCHASE_OPTION_ID)

  const comboValue: OptionType | undefined = selectedNumber
    ? { ...selectedNumber, name: formatPhoneWithLabel(selectedNumber.value, labelForNumber(selectedNumber.value)) }
    : undefined

  useEffect(() => {
    if (!isEditing && selectableOptions.length === 1 && !selectedNumber) {
      setSelectedNumber(selectableOptions[0])
    }
  }, [selectableOptions.length])

  const effectiveDniId = isEditing ? dniData?.id : newDniId
  const snippetCode = effectiveDniId
    ? `<script src="${typeof window !== `undefined` ? window.location.origin : ``}/dni.js" data-id="${
        effectiveDniId
      }"></script>`
    : ``

  // SmartForm swallows a network-level fetch error (its .catch is a no-op) and only
  // calls onPreventDefault when the server returns JSON. Guard against a stuck button
  // by clearing the submitting state after a timeout if no response arrives.
  const onSubmitStart = () => {
    setSubmitting(true)
    window.setTimeout(() => setSubmitting(false), 30_000)
  }

  const onCreateCompleted = (data: { success?: boolean; error?: string }) => {
    debug(`dni`, `onCreateCompleted`, data)
    setSubmitting(false)
    if (data?.success) {
      notifySuccess(`DNI configuration saved. Pool numbers may take a moment to provision.`)
      setClosing(true)
      onAdd()
      closeModal()
    } else {
      notifyError(`Failed to save: ${data?.error || `Unknown error`}`)
    }
  }

  const onDisable = () => {
    setRemoving(true)
    window
      .fetch(`/api/integrations/dni`, {
        method: `POST`,
        credentials: `include`,
        headers: { 'Content-Type': `application/x-www-form-urlencoded` },
        body: new URLSearchParams({
          dniId: dniId || dniData?.id || ``,
          locationId: locationId || location?.value || ``,
          _action: `disable`,
        }).toString(),
      })
      .then(() => {
        notifySuccess(`DNI pool disabled`)
        setClosing(true)
        onAdd()
        closeModal()
      })
      .finally(() => setRemoving(false))
  }

  // Super-user path: buy a new AWS number and select it as the number this pool replaces.
  const onPurchaseNumber = () => {
    const locId = locationId || location?.value
    if (!locId) return
    showModal(
      <IvrProvisioningModal
        locationId={locId}
        onSuccess={(entry) => {
          const newEntry: ManagedNumber = { id: entry.id, phone: entry.phone, label: entry.label }
          setManagedNumbers((prev) => [...prev, newEntry])
          setSelectedNumber({
            id: entry.phone,
            name: formatPhoneWithLabel(entry.phone, entry.label),
            value: entry.phone,
          })
          setAreaCode(extractAreaCode(entry.phone))
        }}
      />,
      { title: `Purchase Number`, isFullScreen: false, useRadix: true },
    )
  }

  return {
    isEditing,
    mode,
    location,
    dniData,
    maxPoolSizeValue,
    poolSizeValue,
    areaCode,
    removing,
    closing,
    submitting,
    busy,
    loadingValues,
    newDniId,
    numberOptions,
    selectedNumber,
    comboValue,
    replacesNumberDisplay: formatPhoneWithLabel(dniData?.forwardTo, labelForNumber(dniData?.forwardTo)),
    snippetCode,
    locationName,
    locationId,
    dniId,
    setMode,
    setLocation,
    setMaxPoolSizeValue,
    setPoolSizeValue,
    setAreaCode,
    setSelectedNumber,
    onSubmitStart,
    onCreateCompleted,
    onDisable,
    onPurchaseNumber,
  }
}
